/**
 * Reading a financier's payout letter.
 *
 * 5 Nov 2026: payment release on an encumbered private-seller deal needs the payout
 * amount, who it goes to and their bank details (encumbranceService.payoutGate), and
 * nothing in the platform captured them. The Purchase Details panel now reads the letter
 * and pre-fills those for a person to check and confirm.
 *
 * This only proposes. Every value it returns is shown in an editable box and nothing is
 * stored until somebody presses Confirm, because these are the figures a bank file pays.
 *
 * Payout letters say the same things in many ways. What this looks for:
 *   amount     "Payout amount", "Payout figure", "Total payout", "Amount required to pay
 *              out", "Settlement amount" - scored, and among equals the largest figure.
 *   valid to   "Valid until", "Good until", "Valid to", "Expires".
 *   bank       BSB and account number together (the account beside or just after the BSB,
 *              never the loan account number, which is the reference).
 *   reference  "Loan account number", "Contract number", "Agreement number", "Reference".
 *   BPAY       Biller code and reference. A bank file cannot pay BPAY, so this is reported
 *              so the screen can say so rather than leave the bank boxes mysteriously empty.
 */

export interface PayoutLetterRead {
  amount: number | null
  recipient: string | null
  bsb: string | null
  accountNumber: string | null
  accountName: string | null
  reference: string | null
  validUntil: string | null // ISO yyyy-mm-dd
  letterDate: string | null // ISO yyyy-mm-dd, the date the letter was written
  bpayBiller: string | null
  bpayReference: string | null
  found: string[]
}

const money = (s: string) => {
  const n = Number(String(s).replace(/,/g, ''))
  return Number.isFinite(n) && n > 0 ? n : null
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

/** dd/mm/yyyy, d-m-yy, or "12 November 2026" to ISO. Australian day-first. */
export function isoDate(s: string): string | null {
  const t = String(s || '').trim()
  let m = t.match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/)
  if (m) {
    const d = Number(m[1])
    const mo = Number(m[2])
    let y = Number(m[3])
    if (y < 100) y += 2000
    if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12) return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  }
  m = t.match(/(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})/)
  if (m) {
    const mo = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase())
    const d = Number(m[1])
    if (mo >= 0 && d >= 1 && d <= 31) return `${m[3]}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  }
  return null
}

const ORG = /\b(pty\.?\s*ltd|limited|ltd|finance|financial|bank|credit\s*union|capital|leasing|lending|money|motor\s*finance)\b/i

export function parsePayoutLetter(raw: string): PayoutLetterRead {
  const lines = String(raw || '')
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
  const found: string[] = []

  /* ---- amount ----
     A payout line often mentions a fee or interest it includes ("Total payout including
     early termination fee: $12,350.00", "Payout amount: $12,480.55 (includes fee of
     $350.00)"). Only the figure that belongs to the fee is ignored - "fee of $350" - never
     the whole line, or the payout falls back to a lower balance and the financier is
     underpaid. Per-day interest lines are not the payout at all. */
  let amount: number | null = null
  {
    const scored: { v: number; rank: number }[] = []
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]
      if (!/pay\s*-?\s*out|settle|amount\s+(?:required|owing|due)|balance\s+(?:owing|due)/i.test(l)) continue
      if (/\bper\s+(?:day|diem)\b|\bdaily\b/i.test(l)) continue
      const amounts = [...l.matchAll(/\$\s*([0-9][0-9,]*(?:\.\d{2})?)/g)].filter((m) => {
        const before = l.slice(Math.max(0, (m.index || 0) - 30), m.index || 0)
        return !/(?:fee|interest|charge|cost)s?\s+(?:of|is|amount\s+of)\s*$/i.test(before)
      })
      let raw: string | null = amounts.length ? amounts[0][1] : null
      if (raw === null && !/\$/.test(l)) {
        const next = lines[i + 1] || ''
        const m = next.match(/^\$?\s*([0-9][0-9,]*\.\d{2})$/)
        if (m) raw = m[1]
      }
      if (raw === null) continue
      const v = money(raw)
      if (v === null) continue
      const rank = /(?:total\s+)?pay\s*-?\s*out\s+(?:amount|figure)|amount\s+required\s+to\s+(?:pay\s*out|settle|discharge)|total\s+payout|settlement\s+amount|payout\s+balance/i.test(l) ? 1 : 2
      scored.push({ v, rank })
    }
    scored.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : b.v - a.v))
    if (scored.length) { amount = scored[0].v; found.push('amount') }
  }

  /* ---- valid until ---- */
  let validUntil: string | null = null
  for (const l of lines) {
    const m = l.match(/(?:valid|good)\s*(?:until|to|through|till)|expir(?:es|y)(?:\s*date)?|payout\s+date/i)
    if (!m) continue
    const d = isoDate(l.slice((m.index || 0) + m[0].length)) || isoDate(l)
    if (d) { validUntil = d; found.push('valid until'); break }
  }

  /* ---- bank: BSB, and the account number beside it ---- */
  let bsb: string | null = null
  let accountNumber: string | null = null
  let accountName: string | null = null
  for (let i = 0; i < lines.length && !bsb; i++) {
    const m = lines[i].match(/\bBSB\b\s*(?:no\.?|number)?\s*[:#]?\s*(\d{3})\s*[- ]?\s*(\d{3})\b/i)
    if (!m) continue
    bsb = m[1] + '-' + m[2]
    for (let j = i; j < Math.min(lines.length, i + 3); j++) {
      const near = (j === i ? lines[j].slice((m.index || 0) + m[0].length) : lines[j])
      /* The loan number is the reference, not where the money goes: "please quote your
         account number 55102938" names the customer's loan. */
      if (/\bloan\b|\bcontract\b|\bagreement\b|\bquote\b|\byour\b|\breference\b|\bref\b/i.test(near) && !/\bBSB\b/i.test(near)) continue
      const a = near.match(/(?:acc(?:ount)?|a\/c)\.?\s*(?:no\.?|number|#)?\s*[:#]?\s*(\d[\d -]{3,14}\d)/i)
      if (a) { accountNumber = a[1].replace(/[\s-]/g, ''); break }
    }
  }
  if (bsb) found.push('BSB')
  if (accountNumber) found.push('account number')
  for (const l of lines) {
    const m = l.match(/acc(?:ount)?\.?\s*name\s*[:#-]?\s*(.+)$/i)
    if (m && m[1].trim().length > 2) { accountName = m[1].replace(/\s+BSB.*$/i, '').trim(); found.push('account name'); break }
  }

  /* ---- reference: the loan, not the bank account ----
     The value must carry a digit: "Reference Number: LN884213" once read as "Number". */
  let reference: string | null = null
  const REF = /(?:loan\s+(?:account\s+)?(?:no\.?|number)|contract\s+(?:no\.?|number)|agreement\s+(?:no\.?|number)|our\s+ref(?:erence)?|(?:payment\s+)?reference(?:\s+(?:no\.?|number))?)\s*[:#]?\s*([A-Z0-9][A-Z0-9/-]{3,})/gi
  for (const l of lines) {
    for (const m of l.matchAll(REF)) {
      if (/\d/.test(m[1])) { reference = m[1]; break }
    }
    if (reference) { found.push('reference'); break }
  }

  /* ---- the letter's own date (not its expiry) ---- */
  let letterDate: string | null = null
  for (const l of lines.slice(0, 12)) {
    if (/valid|good\s+(?:until|to)|expir|until|\bdob\b|birth/i.test(l)) continue
    const d = isoDate(l)
    if (d) { letterDate = d; break }
  }

  /* ---- BPAY ---- */
  let bpayBiller: string | null = null
  let bpayReference: string | null = null
  for (const l of lines) {
    const b = l.match(/biller\s*code\s*[:#]?\s*(\d{3,10})/i)
    if (b) bpayBiller = b[1]
    const r = l.match(/(?:bpay\s+)?(?:customer\s+)?ref(?:erence)?\s*(?:no\.?|number)?\s*[:#]?\s*(\d{6,20})\b/i)
    if (r && /bpay|biller|ref/i.test(l) && bpayBiller !== null) bpayReference = r[1]
  }
  if (bpayBiller) found.push('BPAY')

  /* ---- who it goes to ---- */
  let recipient: string | null = null
  for (const l of lines.slice(0, 8)) {
    if (ORG.test(l) && !/\bdear\b|\bre:|\babn\b|\bphone\b|\bemail\b|@|www\.|\bpo box\b|\d{4}\s*$/i.test(l) && l.length <= 60) {
      recipient = l.replace(/[,.]$/, '')
      break
    }
  }
  if (!recipient && accountName && ORG.test(accountName)) recipient = accountName
  if (recipient) found.push('financier')

  return { amount, recipient, bsb, accountNumber, accountName, reference, validUntil, letterDate, bpayBiller, bpayReference, found }
}
